import AlternativeFeature from "@components/alternatives/AlternativeFeature";
import { AlternativeInformation } from "@components/alternatives/AlternativeInformation";
import { AlternativesCards } from "@components/alternatives/AlternativesCards";
import { AlternativesCTASection } from "@components/alternatives/AlternativesCTASection";
import { AlternativesTopBannerCTASection } from "@components/alternatives/AlternativesTopBannerCTASection";
import { ContentParagraph } from "@components/alternatives/ContentParagraph";
import { Herosection, HeroSectionProps } from "@components/alternatives/HeroSection";
import { OverviewContent, OverviewGrid } from "@components/alternatives/OverviewGrid";
import AwardsSection from "@components/new-ui/AwardsSection";
import CTASection from "@components/new-ui/CTASection";
import NewLayout from "@components/new-ui/Layout";
import { ClientFeedback, Navbar } from "@pages/case-study/alrannan-administrative-services";
import Image from "next/image";
import React from "react";
import { alternativesCardsDetails, ZixflowNavLinks } from "../../components/alternatives/constant";
import { AlternativesInfo } from "./interakt";
import { extractTextFromJSX } from "@utils/schemaGenerator";

const herosectionData: HeroSectionProps = {
    chipTitle: "Alternatives",
    title: "Top 10 Zixflow Alternatives for WhatsApp Automation in 2025",
    primaryButtonText: "Try for Free",
    imageUrl: "https://assets.gallabox.com/gb-home/alternatives/zixflow/zixflow-hero.png",
    imgAlt: "doubletick",
};

const gallabox: AlternativesInfo[] = [
    {
        id: "1",
        title: "1. Gallabox",
        description: (
            <div className="text-gray-900 text-lg">
                <p className="text-gray-900 text-lg pb-6">Best for end-to-end customer lifecycle management on WhatsApp</p>
            </div>
        ),
        image: "https://assets.gallabox.com/gb-home/alternatives/zixflow/build-bot.png",
        imgAlt: "build-bot",
        list: [
            { item: "Build bots quickly by giving Gallabox simple, conversational prompts" },
            {
                item: (
                    <>
                        If you’re looking for a WhatsApp automation tool that does more than just basic broadcasts, Gallabox is a solid Zixflow alternative. It offers a
                        <a href="https://gallabox.com/whatsapp-chatbot" className="underline px-1" style={{ color: "#3364FA" }}>
                            no-code chatbot builder,
                        </a>
                        AI-powered automation, and more — making it easy to create engaging, multi-step customer journeys without technical headaches.
                    </>
                ),
            },
            {
                item: (
                    <>
                        Unlike Zixflow, Gallabox provides
                        <a href="https://gallabox.com/blog/whatsapp-multiple-agent" className="underline px-1" style={{ color: "#3364FA" }}>
                            multi-agent support,
                        </a>
                        conversational chatbots, and auto-assigned tickets, ensuring your team can manage high volumes of customer conversations faster. Plus, 40+ integrations mean
                        your CRM, e-commerce platform, and helpdesk all stay in sync — no data silos, no extra work.
                    </>
                ),
            },
        ],
    },
];

const interakt: AlternativesInfo[] = [
    {
        id: "2",
        title: "2. Interakt",
        image: "https://assets.gallabox.com/gb-home/alternatives/zixflow/interakt.png",
        imgAlt: "interakt",
        description: "Best for SMBs requiring a simple and affordable WhatsApp and Instagram automation tool",
        list: [
            {
                item: "Interakt is another WhatsApp Business API solution that allows you to manage customer conversations, send automated messages, and run marketing campaigns on WhatsApp. More recently, they’ve also started supporting basic Instagram automations like answering DMs, “price please” comments, and adding new followers to a giveaway contest list.",
            },
            {
                item: "Also, unlike Zixflow, Interakt supports advanced WhatsApp automations like click-to-WhatsApp ads and WhatsApp Commerce — not just WhatsApp broadcasts. ",
            },
        ],
    },
];

const wati: AlternativesInfo[] = [
    {
        id: "3",
        title: "3. Wati",
        description: "Best for businesses requiring custom WhatsApp chatbots",
        image: "https://assets.gallabox.com/gb-home/alternatives/zixflow/wati.png",
        imgAlt: "wati",
        list: [
            {
                item: "Wati is another popular Zixflow alternative for businesses requiring advanced WhatsApp automations like AI-supported agents and complex chatbots. The Wati custom bots can automate up to 200 steps.",
            },
            {
                item: "Wati has also started supporting Facebook Messenger and Instagram DMS. No automations, your messages will just be added to the shared inbox for central access across the team. Can be helpful if you don’t have a social media platform to track messages.",
            },
        ],
    },
];

const hubspotSalesHub: AlternativesInfo[] = [
    {
        id: "4",
        title: "4. Hubspot Sales Hub",
        image: "https://assets.gallabox.com/gb-home/alternatives/zixflow/hubspot-sales-hub.png",
        imgAlt: "hubspot-sales-hub",
        description: "Best CRM for B2B companies requiring a generous free plan",
        list: [
            {
                item: "HubSpot Sales Hub is a sales CRM built to track leads, automate follow-ups, and close deals faster. It’s great for small to mid-sized businesses, B2B sales teams, and anyone using HubSpot’s Marketing Hub. If you need a simple, all-in-one tool to manage your pipeline (for free), Hubspot is definitely worth a look.",
            },
            {
                item: "And unlike Zixflow, Hubspot Sales Hub comes with deeper automations and lead management tools. Not to mention integrations.",
            },
        ],
    },
];

const activeCampaign: AlternativesInfo[] = [
    {
        id: "5",
        title: "5. ActiveCampaign",
        image: "https://assets.gallabox.com/gb-home/alternatives/zixflow/activeCampaign.png",
        imgAlt: "activeCampaign",
        description: "Best marketing automation and sales CRM platform",
        list: [
            {
                item: "ActiveCampaign is a Zixflow alternative that does many things as well — email marketing, social ad management, SMS marketing, and even a sales CRM with advanced pipeline management features. So if you’re looking for an all-in-one Zixflow alternative for outreach and marketing automation, then we definitely recommend it. ",
            },
        ],
    },
];

const twilioflex: AlternativesInfo[] = [
    {
        id: "6",
        title: "6. Twilio Flex",
        image: "https://assets.gallabox.com/gb-home/alternatives/zixflow/twilio-flex.png",
        imgAlt: "twilio-flex",
        description: "Best for enterprises requiring centralized customer support",
        list: [
            {
                item: "Twilio Flex is a “programmable” contact center platform that allows businesses to unify and customize customer interactions across various communication channels, including voice, SMS, email, and chat. You can even allow customers to message you directly from social ads with click-to-message CTAs.",
            },
            {
                item: "Unlike ZixFlow, which only supports a few workflow automations, Twilio Flex allows for extensive customization. Plus, it has AI features — via Concierge — for advanced reporting and conversation routing.",
            },
        ],
    },
];

const gupshup: AlternativesInfo[] = [
    {
        id: "7",
        title: "7. Gupshup",
        image: "https://assets.gallabox.com/gb-home/alternatives/zixflow/gupshup.png",
        imgAlt: "gupshup",
        description: "Best omnichannel, agentic chatbot platform",
        list: [
            {
                item: "Gupshup is a popular conversational chatbot tool that’s great for any business that’s exploring agentic AI for customer interactions. Not only does it support over 30 messaging channels, but its chatbots are also industry-trained, so they can handle industry-specific queries right out of the box. ",
            },
        ],
    },
];

const trengo: AlternativesInfo[] = [
    {
        id: "8",
        title: "8.  Trengo",
        image: "https://assets.gallabox.com/gb-home/alternatives/zixflow/trengo.png",
        imgAlt: "trengo",
        description: "Best for businesses requiring multilingual help centers",
        list: [
            {
                item: "Like Gupshup, Trengo also supports multiple messaging channels, bringing WhatsApp, email, Instagram, live chat, and more into a single inbox. It’s also got strong agentic AI capabilities. But where it really stands out is its team collaboration features. This includes internal notes, intelligent conversation routing, and collision detection to flag if two people are answering the same conversation.",
            },
        ],
    },
];

const keap: AlternativesInfo[] = [
    {
        id: "9",
        title: "9. Respond.io",
        image: "https://assets.gallabox.com/gb-home/alternatives/zixflow/keap.png",
        imgAlt: "keap",
        description: "Best sales and marketing automation platform for franchises",
        list: [
            {
                item: "Keap is a CRM and marketing automation platform designed primarily for small businesses. Though its primary focus is customer engagement via emails, it also supports text automations. It’s also one of the few CRMs that allows franchisors to unify all franchisees on a single platform.",
            },
        ],
    },
];

const pipedrive: AlternativesInfo[] = [
    {
        id: "10",
        title: "10. Pipedrive",
        image: "https://assets.gallabox.com/gb-home/alternatives/zixflow/pipedrive.png",
        imgAlt: "pipedrive",
        description: "Best Sales CRM for small businesses",
        list: [
            {
                item: "Pipedrive is another solution that initially began as a sales CRM for lead management. Today, it also supports basic email marketing and project management — making it an affordable all-in-one tool for sales teams.",
            },
            { item: "A standout feature is its AI CRM, which acts as your sales team’s virtual assistant — helping them prioritize tasks, analyze leads, and more." },
        ],
    },
];

const content: OverviewContent = {
    title: ["Platform", "Tool type", "Standout feature", "Pricing"],
    rows: [
        [["Gallabox"], ["WhatsApp automation"], ["AI-powered messaging and automation"], ["Starts at $89 per year (3 users) / INR 2,399 per year (3 users); free trial available"]],
        [["Trengo"], ["Marketing automation and sales CRM"], ["AI assistants for content and image generation"], ["Starts at $249 per month; free trial available"]],
        [["Interakt"], ["WhatsApp and Instagram automation"], ["Custom replies to Instagram DMs"], ["Starts at $17 per month; free trial available"]],
        [["Wati"], ["WhatsApp, Facebook Messenger, Instagram DM automation"], ["Custom AI chatbots"], ["Starts at $39 per month; free trial available"]],
        [["Hubspot"], ["Sales CRM"], ["Quote-based sales workflows"], ["Free plan available"]],
        [["ActiveCampaign"], ["Marketing automation and sales CRM"], ["AI lead scoring"], ["Starts at $15 per month; free trial available"]],
        [["Twilio Flex"], ["Contact center platform"], ["Can customize the entire experience"], ["Starts at $1.0 per hour; free trial available"]],
        [["Gupshup"], ["AI conversational chatbots"], ["Industry-specific agentic AI templates"], ["Custom pricing; no free trial"]],
        [["Pipedrive"], ["Sales CRM"], ["AI prospecting tool"], ["Starts at $14 per month; free trial available"]],
    ],
};

const allAlternatives: AlternativesInfo[] = [...gallabox, ...interakt, ...wati, ...hubspotSalesHub, ...activeCampaign, ...twilioflex, ...gupshup, ...trengo, ...keap, ...pipedrive];

const simplifiedAlternatives: { title: string; description: string; imageUrl: string }[] = allAlternatives.map(({ title, description, image }) => ({
    title,
    description: extractTextFromJSX(description),
    imageUrl: image as string,
}));

const ZixFlow: React.FC = () => {
    return (
        <NewLayout
            title="10 Best Zixflow Alternatives for 2025 | Gallabox Blog"
            description="Explore the top 10 Zixflow alternatives. Compare features, understand limitations, and choose a platform that works best for your requirements. "
            canonical="https://gallabox.com/alternatives/zixflow"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/alternatives/zixflow/zixflow-og.png" }}
            schema={{
                heroOrOgImageAlt: "alternative",
                heroOrOgImageDescription: "Top 10 Zixflow Alternatives for WhatsApp Automation in 2025",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/alternatives/zixflow/zixflow-hero.png",
                alternativesInfo: simplifiedAlternatives,
            }}
        >
            <Herosection {...herosectionData} />

            <section className="max-w-6xl md:mx-36 mx-auto md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <p className="text-gray-900 text-xl font-semibold pb-5">Table of contents</p>
                        <Navbar links={ZixflowNavLinks} />
                    </div>
                </div>

                <div className="col-span-4 md:pl-4 px-4">
                    <ContentParagraph
                        content={[
                            "On average, small businesses use 20-50 software tools (Source: Cin7). No wonder, many software vendors are going the “all-purpose tool” route and adding more and more features. But the question is: do such all-in-one tools really work?",
                            "In this article, we’re analyzing Zixflow to see whether it truly delivers on its promise. Positioned as an all-in-one tool for outreach and lead management, Zixflow also gives you built-in CRM features, multi-channel campaigns across email, SMS, and WhatsApp, and more.",
                            "But when a tool tries to do everything, it often fails to do the important things well. With Zixflow, you’ll see this in the lack of feature depth (for e.g. no conversational chatbots), too few integrations (and no webhook triggers; only actions), and quite a few UI quirks.",
                            "Let’s explore its strengths, where it falls short, and the best Zixflow alternatives if you need a more specialised solution for WhatsApp automation, CRM, or email marketing.",
                        ]}
                    />

                    <h2 className="text-black text-5xl font-semibold pb-8 mt-10">Why look for a Zixflow alternative?</h2>
                    <ContentParagraph
                        content={[
                            "Still wondering if you should make the switch from Zixflow to a different — more specialized — alternative. We’ve gone into more detail about where Zixflow falls short, so you can make an informed decision.",
                            "Because, like we said at the beginning, Zixflow covers a lot — but does it do them well?",
                        ]}
                    />

                    <ContentParagraph
                        title="Lacks feature depth"
                        content={[
                            "Let’s look at Zixflow’s capabilities and how they compare to market leaders when it comes to features. To start with, here are some not-so-advanced features that Zixflow lacks:",
                        ]}
                    />

                    <AlternativeFeature
                        featuresData={{
                            title: "Lacks feature depth",
                            featuresList: [
                                {
                                    item: (
                                        <p className="py-2 text-lg">
                                            Let’s look at Zixflow’s capabilities and how they compare to market leaders when it comes to features. To start with, here are some
                                            not-so-advanced features that Zixflow lacks:
                                        </p>
                                    ),
                                },
                                {
                                    item: " No lead scoring options in CRM — so despite being an outreach platform, there’s no way to prioritise high-impact leads.",
                                },
                                {
                                    item: "No conditional logic in forms: Despite the modern, “one-question-at-a-time” form UI, Zixflow’s forms don’t allow you to personalise conversations based on the answers to the previous question.",
                                },
                                {
                                    item: "No auto-ticket-assignment in the shared inbox: There’s no option to assign conversations to a particular user — a feature that’s quite basic in most customer interaction tools.",
                                },
                                {
                                    item: "No two-way, conversational chatbots in WhatsApp: Zixflow allows you to create simple no-code bots via WhatsApp Flows. The sequence usually stops when someone messages back; there’s no AI capability to take the conversation forward. ",
                                },
                            ],
                        }}
                    />

                    <ContentParagraph
                        content={[
                            "And as these are pretty basic features, not having them means fragmented workflows. As you scale, you may find yourself needing to invest in other tools to fill the gaps, like a help desk platform to manage customer interactions or a CRM for intelligent lead management.",
                        ]}
                    />

                    <ContentParagraph
                        title="Lacks integrations"
                        content={
                            <>
                                <p className="text-lg pb-6 text-left">
                                    Like with features, Zixflow also comes with a pretty short list of direct integrations. And while it does integrate with Zapier, there are no
                                    triggers, only actions. So, while you can send data to Zixflow, you can’t send data from it. This can be a huge drawback if you plan to
                                    supplement Zixflow with a more advanced tool for some added functionality.
                                    <Image
                                        width={800}
                                        height={450}
                                        src="https://assets.gallabox.com/gb-home/alternatives/zixflow/integrations.png"
                                        alt="integrations"
                                        className="py-5"
                                    />
                                    Here’s an example: Let’s say you run an e-commerce store and use Zixflow for WhatsApp marketing. But you’re supplementing Zixflow with Zendesk
                                    for customer support. Through Zixflow, you send an automated message confirming a customer’s order. But the customer replies with an issue.
                                    You’ll have to manually create a ticket in Zendesk, as Zixflow doesn’t have triggers.
                                </p>
                                <p className="text-lg pb-6 text-left">
                                    Similarly, you won’t be able to trigger a Slack message, add new contacts to Google Sheets, etc., because you can’t send data from Zixflow.
                                </p>
                            </>
                        }
                    />

                    <ContentParagraph
                        title="Usability hiccups"
                        content={
                            <>
                                <p className="text-lg pb-6 text-left">
                                    Some users on review sites like G2 have mentioned that setting up campaigns, APIs, and SMS accounts was time-consuming, and they needed to
                                    contact the Zixflow team for support.
                                </p>
                                <p className="text-lg pb-6 text-left">
                                    You might also encounter similar UX challenges when exploring other features. For example, there’s a conceptual overlap between “Collections”
                                    and “Lists”. With both being used to organise contacts, new users might find it difficult to know when to use what.
                                    <Image width={800} height={450} src="https://assets.gallabox.com/gb-home/alternatives/zixflow/dashboard.png" alt="dashboard" className="py-5" />
                                    Similarly, Zixflow’s AI doesn’t cover reporting — you get only basic charts, and setting them up is a multi-step, manual process: Create Insight
                                    (dashboard) &gt; Create Report &gt; Create Chart. Want the same chart in two reports? Not possible. Same report in a different dashboard? Also
                                    not possible. Not ideal for building detailed reports.
                                </p>
                            </>
                        }
                    />

                    <ClientFeedback
                        descriptions={
                            <p className="text-gray-900 italic font-semibold px-6 mb-4 text-center">
                                TL;DR: Zixflow is great if you want to move away from multiple tools, but with its limited features, you’ll either have to make do with what’s there
                                or use manual workarounds to get things done.
                            </p>
                        }
                        className="border-white-500 border-2 "
                    />

                    <h2 className="text-black text-5xl font-semibold pb-8 mt-10">Best Zixflow Alternatives at a Glance</h2>
                    <ContentParagraph
                        content={["Let’s start with a dive into Zixflow’s top competitors — each excelling in their own niche. But first, here’s everything at a glance:"]}
                    />

                    <OverviewGrid data={content} />

                    <p className="text-xl">Note: The prices listed in the article are the monthly cost if you choose the annual billing option.</p>

                    <div className="flex flex-col mt-10">
                        <section id="Gallabox">
                            <AlternativeInformation data={gallabox} />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Gallabox’s best features",
                                    featuresList: [
                                        {
                                            item: "GPT-powered chatbots: Run automated, two-way conversations by integrating your WhatsApp chatbots with ChatGPT. ",
                                        },
                                        {
                                            item: "Audience segmentation: Group customers by demographics, behaviour, or purchase history to deliver highly targeted campaigns.",
                                        },
                                        {
                                            item: "Gallabox GenAI: Generate personalised message drafts instantly, helping agents save time and maintain a consistent brand voice.",
                                        },
                                        {
                                            item: "WhatsApp Payments: Drive more sales by allowing customers to pay right from within the WhatsApp UI.",
                                        },
                                        {
                                            item: "Auto-assign tickets: Route incoming chats to the right agent based on availability, expertise, or predefined rules for faster resolution.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Gallabox’s limitations",
                                    featuresList: [
                                        {
                                            item: "New Gallabox users may experience an initial learning curve.",
                                        },
                                        { item: "It supports only WhatsApp and no other messaging channel." },
                                    ],
                                }}
                            />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Gallabox’s pricing",
                                    featuresList: [
                                        {
                                            item: "Starts at $89 per year for up to 3 users. Gallabox also offers a free trial and a custom plan with advanced WhatsApp automations for large teams with over 25 users.",
                                        },
                                    ],
                                }}
                            />
                            <ContentParagraph
                                content={
                                    <p className="text-lg pt-3 pb-5">
                                        <a href="https://app.gallabox.com/signup" className="underline px-1" style={{ color: "#3364FA" }}>
                                            Start your free trial today!
                                        </a>
                                    </p>
                                }
                            />

                            <ClientFeedback
                                descriptions={
                                    <p className="text-gray-900 italic font-semibold px-6 mb-4">
                                        Gallabox streamlined communication, improved customer service, and increased productivity by 30%. It integrates with CRM, tracks customer
                                        interactions, and is user-friendly.
                                    </p>
                                }
                                authorName="– Hazim Nazir, Quality and Audit Officer, QIC"
                                className="border-white-500 border-2 "
                            />
                        </section>

                        <section id="Interakt" className="pt-10">
                            <AlternativeInformation data={interakt} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Interakt’s best features",
                                    featuresList: [
                                        {
                                            item: "AnswerBot: Automate customer questions with a ChatGPT-powered WhatsApp bot.",
                                        },
                                        {
                                            item: "No-code bots: Build bots and integrate them with your tech stack to ensure your customer data is synced across the board.",
                                        },
                                        {
                                            item: "Concierge: Use Interakt’s AI tool to analyze which campaigns have the best click-through rates to improve your ROAS.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Interakt’s limitations",
                                    featuresList: [
                                        {
                                            item: "App integrations depend on your subscription level. The Starter plan only supports Shopify, while Growth gives you 3 integrations. You’ll need to subscribe to Advanced or Enterprise for more integrations.",
                                        },
                                        { item: "For ecommerce users, it doesn’t support product browsing abandonment tracking, so you can’t re-engage potential customers." },
                                        { item: "Multiple Shopify store owners have found Interakt’s customer support team slow to respond and even unhelpful at times." },
                                    ],
                                }}
                            />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Interakt’s pricing",
                                    featuresList: [
                                        {
                                            item: "Starter: $17 per month (billed annually)",
                                        },
                                        {
                                            item: "Growth: $40 per month (billed annually)",
                                        },
                                        {
                                            item: "Advanced: $50 per month (billed annually)",
                                        },
                                        {
                                            item: "Enterprise: Custom pricing",
                                        },
                                    ],
                                }}
                            />
                        </section>

                        <section id="Wati" className="pt-10">
                            <AlternativeInformation data={wati} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Wati’s best features",
                                    featuresList: [
                                        {
                                            item: "Chatbot timer: When using AI bots, you can set a 24-hour timer for customers to respond. If they don’t, then the chat will be automatically closed.",
                                        },
                                        {
                                            item: "Team inbox analytics: Get reports on ticket status, time to respond, and team performance.",
                                        },
                                        {
                                            item: "Send catalogs via keyword action: Automatically send product catalog when customers type certain keywords.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Wati’s limitations",
                                    featuresList: [
                                        {
                                            item: "Chats automatically close if not responded to within 24 hours, so missed conversations can’t be reopened.",
                                        },
                                        {
                                            item: "It can be expensive for small businesses, especially if you plan to send a lot of WhatsApp marketing messages.",
                                        },
                                        {
                                            item: "You’ll need to upgrade to the Pro plan to use automations (including drip campaigns) — which is double the cost of the base plan.",
                                        },
                                    ],
                                }}
                            />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Wati’s pricing",
                                    featuresList: [
                                        {
                                            item: "Growth: $39 per month for 5 users",
                                        },
                                        {
                                            item: "Pro: $79 per month for 5 users",
                                        },
                                        {
                                            item: "Business: $199 per month for 5 users",
                                        },
                                    ],
                                }}
                            />
                        </section>

                        <section id="Hubspot Sales Hub" className="pt-10">
                            <AlternativeInformation data={hubspotSalesHub} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Hubspot’s best features",
                                    featuresList: [
                                        {
                                            item: "Canned snippets: Create a knowledge base of up to 5000 FAQs to add to live chats and emails.",
                                        },
                                        {
                                            item: "Prospecting agent: Build a high-quality sales pipeline with an AI-driven prospecting agent that crafts personalized outreach.",
                                        },
                                        {
                                            item: "Sales automation: Set up to 300 workflows, including quote-based ones.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Hubspot’s limitations",
                                    featuresList: [
                                        {
                                            item: "If you’re locked into a certain subscription period — especially post-discount — you’ll have to stick to it throughout, even if you don’t have that many users.",
                                        },
                                        {
                                            item: "Some features, like Tasks (can’t filter based on task title) and Invoices (can’t create credit notes from an invoice), can be limited.",
                                        },
                                        { item: "It’s limited to only lead management, no marketing automations. For that, you need to subscribe to Hubspot Marketing Hub. " },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Hubspot’s pricing",
                                    featuresList: [
                                        {
                                            item: "Free",
                                        },
                                        {
                                            item: "Sales Hub: $90 per user, per month (billed annually)",
                                        },
                                        { item: "Sales Hub Enterprise: $150 per user, per month (billed annually)" },
                                    ],
                                }}
                            />
                        </section>

                        <section id="ActiveCampaign" className="pt-10">
                            <AlternativeInformation data={activeCampaign} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "ActiveCampaign’s best features",
                                    featuresList: [
                                        {
                                            item: "Win probability: Gauge how likely it is to close a deal with AI-powered predictive analytics.",
                                        },
                                        {
                                            item: "Split testing: A/B test your marketing campaigns to figure out which ones perform better.",
                                        },
                                        {
                                            item: "AI automations: Describe your workflow in plain English and let AI build your automation.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "ActiveCampaign’s limitations",
                                    featuresList: [
                                        {
                                            item: "Some features (like dynamic content or custom objects, to name a few) may be a little overkill for businesses planning to run simple email campaigns.",
                                        },
                                        {
                                            item: "It’s a little complex for new users, especially as there’s not much product training available.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "ActiveCampaign’s pricing",
                                    featuresList: [
                                        {
                                            item: "Starter: $15 per month for 1 user",
                                        },
                                        {
                                            item: "Plus: $49 per month for 1 user",
                                        },
                                        {
                                            item: "Pro: $79 per month for 3 users",
                                        },
                                        {
                                            item: "Enterprise: $145 per month for 5 users",
                                        },
                                    ],
                                }}
                            />
                        </section>

                        <section id="Twilio Flex" className="pt-10">
                            <AlternativeInformation data={twilioflex} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Twilio Flex’s best features",
                                    featuresList: [
                                        {
                                            item: "Omnichannel support: It supports voice calls, SMS, email, chat (like WhatsApp and Facebook Messenger), and even custom channels such as in-app chat and video.",
                                        },
                                        {
                                            item: "Programmability: You can customize every aspect of the platform — from the user interface to interaction workflows like call flows and routing logic.",
                                        },
                                        {
                                            item: "Intelligent routing: Analyze a caller’s past interactions and CRM data to route calls and chats to the best appropriate agent.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Twilio Flex’s limitations",
                                    featuresList: [
                                        {
                                            item: "Some users find the reporting features to be quite complex.",
                                        },
                                        {
                                            item: "As Twilio Flex operates on a pay-as-you-go model, costs can quickly add up — especially if you add in the time it takes to customize the platform.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Twilio Flex’s pricing",
                                    featuresList: [
                                        {
                                            item: "Per-hour pricing: Starts at $1.00 per active user hour",
                                        },
                                        {
                                            item: "Per-user pricing: Starts at $150 per named user",
                                        },
                                    ],
                                }}
                            />
                        </section>

                        <section id="Gupshup" className="pt-10">
                            <AlternativeInformation data={gupshup} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Gupshup’s best features",
                                    featuresList: [
                                        {
                                            item: "AI agent library: Access a library of 15 pre-built AI agents that are customized for specific industries.",
                                        },
                                        {
                                            item: "Conversation builder: Build custom agentic bots with Gupshup’s no-code/low-code bot builder.",
                                        },
                                        {
                                            item: "Customer segmentation: Group your audience based on various criteria, allowing for targeted messaging and personalized interactions.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Gupshup’s limitations",
                                    featuresList: [
                                        {
                                            item: "There’s no central dashboard for all team members.",
                                        },
                                        {
                                            item: "It doesn’t provide a mobile app.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Gupshup’s pricing",
                                    featuresList: [
                                        {
                                            item: "Contact Gupshup for custom pricing.",
                                        },
                                    ],
                                }}
                            />
                        </section>

                        <section id=" Trengo" className="pt-10">
                            <AlternativeInformation data={trengo} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Trengo’s best features",
                                    featuresList: [
                                        {
                                            item: "Omnichannel inbox: Manage all conversations from all messaging channels in one central inbox.",
                                        },
                                        {
                                            item: "Multilingual support: Create a multilingual chatbot and help center in 70+ languages so customers can interact in their own language.",
                                        },
                                        {
                                            item: "Native apps: Access Trengo any way you want with native apps for not just iOS and Android but also macOS and Windows.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Trengo’s limitations",
                                    featuresList: [
                                        {
                                            item: "It lacks some “organization” features like ordering labels and sorting email rules.",
                                        },
                                        {
                                            item: "Some users have reported syncing issues with the Gmail inbox.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Trengo’s pricing",
                                    featuresList: [
                                        {
                                            item: "Boost: $168 per month for 5 users",
                                        },
                                        {
                                            item: "Pro: $281 per month for 5 users",
                                        },
                                        {
                                            item: "Enterprise: Custom pricing",
                                        },
                                    ],
                                }}
                            />
                        </section>

                        <section id="Keap" className="pt-10">
                            <AlternativeInformation data={keap} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Keap’s best features",
                                    featuresList: [
                                        {
                                            item: "Appointment scheduling: Use the built-in appointment scheduler — that integrates with Google and Outlook calendars — to help team members manage bookings and automate follow-ups.",
                                        },
                                        {
                                            item: "Automation builder: Set custom workflows to automate repetitive tasks like lead capture and follow-ups.",
                                        },
                                        {
                                            item: "AI assistants: Use the AI Automation Assistant to generate campaign assets (emails, landing pages) and AI Content Assistant for personalized email communications.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Keap’s limitations",
                                    featuresList: [
                                        {
                                            item: "Pipelines can only be assigned a dollar value — you can’t visualize value by contract or sales volume.",
                                        },
                                        {
                                            item: "Though Keap is positioned as a solution for small businesses, $299 for two users can be expensive.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Keap’s pricing",
                                    featuresList: [
                                        {
                                            item: "Starts at $249 per month for 2 users and 1500 contacts, billed annually.",
                                        },
                                    ],
                                }}
                            />
                        </section>

                        <section id="Pipedrive" className="pt-10">
                            <AlternativeInformation data={pipedrive} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Pipedrive’s best features",
                                    featuresList: [
                                        {
                                            item: "Visual sales pipeline: Get a clear, visual representation of the sales pipeline to track deals at each stage.",
                                        },
                                        {
                                            item: "Pipedrive Pulse: An AI sales prospecting tool, Pulse can score leads and suggest follow-up actions.",
                                        },
                                        {
                                            item: "Lead inbox: Store incoming leads in a separate inbox until you qualify them.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Pipedrive’s limitations",
                                    featuresList: [
                                        {
                                            item: "Custom fields will not be included in AI-generated reports. You also can’t generate pie charts, bar charts, tables, or scorecards with AI. These have to be done manually.",
                                        },
                                        {
                                            item: "Some features lack depth. For example, follow-up automation exists, but email threading isn’t supported. Similarly, you can drag and drop tasks in the calendar for reassignment only within the same week.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Pipedrive’s pricing",
                                    featuresList: [
                                        {
                                            item: "Essential: $14 per user, per month",
                                        },
                                        {
                                            item: "Advanced: $39 per user, per month",
                                        },
                                        {
                                            item: "Professional: $49 per user, per month",
                                        },
                                        {
                                            item: "Power: $64 per user, per month",
                                        },
                                        { item: "Enterprise: $99 per user, per month" },
                                    ],
                                }}
                            />
                            <ContentParagraph
                                content={
                                    <p className="text-lg pt-2">On top of the platform subscription fee, Zoko also charges an additional “fair use” fee depending on your usage.</p>
                                }
                            />
                        </section>
                    </div>
                </div>

                <AlternativesTopBannerCTASection />
            </section>

            <div className="max-w-6xl mx-auto py-8 px-2">
                <h2 className="text-black text-4xl font-semibold pb-5">Choose the Right Zixflow Alternative to Elevate Your Customer Conversations</h2>

                <ContentParagraph
                    content={
                        <p className="text-lg pt-2">
                            If you’re looking for a best-of-breed
                            <a href="https://gallabox.com/whatsapp-business-api" className="underline px-1" style={{ color: "#3364FA" }}>
                                WhatsApp Business API solution
                            </a>
                            to engage customers, then
                            <a href="https://gallabox.com/" className="underline px-1" style={{ color: "#3364FA" }}>
                                Gallabox
                            </a>
                            can be a solid alternative to Zixflow. With 40 million+ chatbot conversations and 20,000+ users, it’s helped businesses of all sizes (and across
                            industries) scale WhatsApp automation with ease.
                        </p>
                    }
                />
                <ContentParagraph
                    content={
                        <p className="text-lg pt-2">
                            From AI-powered chatbots to deep workflow customization, you can tailor everything — whether it’s a simple welcome message or a multi-stage campaign.
                            Plus, with
                            <a href="https://gallabox.com/whatsapp-integrations" className="underline px-1" style={{ color: "#3364FA" }}>
                                20+ integrations,
                            </a>
                            it fits right into your tech stack.
                        </p>
                    }
                />

                <ContentParagraph
                    content={
                        <p className="text-lg pt-2">
                            <a href="https://app.gallabox.com/signup" className="underline px-1" style={{ color: "#3364FA" }}>
                                Sign up for a free trial
                            </a>
                            and see for yourself how Gallabox can help you scale your WhatsApp engagement — from lead nurturing to customer feedback collection and more.
                        </p>
                    }
                />
            </div>

            <AlternativesCTASection />

            <section className="bg-gray-100 md:py-20 py-10">
                <div
                    className="text-gray-900 max-w-6xl mx-auto mb-10 mt-5 md:ml-32 text-center md:text-6xl text-4xl font-semibold  md:text-left"
                    style={{ fontFamily: "Degular Display" }}
                >
                    Related Alternatives
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto gap-y-10">
                    {alternativesCardsDetails
                        .filter((data) => data.id !== "zixflow")
                        .map((data, idx) => (
                            <AlternativesCards key={idx} {...data} />
                        ))}
                </div>
            </section>

            <AwardsSection />

            <CTASection title="Turn connections into conversions" description="Take Gallabox for a 7-day ride" />
        </NewLayout>
    );
};

export default ZixFlow;
